import React from 'react';
import "./WorkyMainDemoOpsShiftManagementScreen.css";
import { Plus } from 'lucide-react';

interface GanttDateMarker {
    readonly label: string;
    readonly day: number;
}

interface GanttShiftBlock {
    readonly startDay: number;
    readonly endDay: number;
    readonly timeRange: string;
    readonly taskName: string;
    readonly subLabel: string;
    readonly isActive: boolean;
}

interface GanttWorkerRow {
    readonly id: string;
    readonly name: string;
    readonly avatarInitials: string;
    readonly avatarBg: string;
    readonly shifts: readonly GanttShiftBlock[];
}

interface GanttColumn {
    readonly startDay: number;
    readonly endDay: number;
}

// ── Gantt helpers ───────────────────────────────────────────────────────────

const GANTT_SEGMENT_DAYS: readonly number[] = [0, 5, 10, 23, 33, 43, 59, 64, 74];

const dayToEqualNum = (day: number): number => {
    const segmentWidth: number = 100 / (GANTT_SEGMENT_DAYS.length - 1);
    for (let i: number = 0; i < GANTT_SEGMENT_DAYS.length - 1; i++) {
        const segStart: number = GANTT_SEGMENT_DAYS[i] ?? 0;
        const segEnd: number = GANTT_SEGMENT_DAYS[i + 1] ?? 74;
        if (day >= segStart && day <= segEnd) {
            const progress: number = (day - segStart) / (segEnd - segStart);
            return (i + progress) * segmentWidth;
        }
    }
    return 100;
};

const toGanttLeft = (day: number): string => `${dayToEqualNum(day).toFixed(2)}%`;
const toGanttWidth = (startDay: number, endDay: number): string =>
    `${(dayToEqualNum(endDay) - dayToEqualNum(startDay)).toFixed(2)}%`;

const GANTT_COLUMNS: readonly GanttColumn[] = GANTT_SEGMENT_DAYS.slice(0, -1).map(
    (startDay: number, i: number): GanttColumn => ({
        startDay,
        endDay: GANTT_SEGMENT_DAYS[i + 1] ?? startDay,
    })
);

// ── Static data ─────────────────────────────────────────────────────────────

const GANTT_DATE_MARKERS: readonly GanttDateMarker[] = [
    { label: 'Feb 15', day: 0 },
    { label: 'Feb 20', day: 5 },
    { label: 'Feb 25', day: 10 },
    { label: 'Mar 10', day: 23 },
    { label: 'Mar 20', day: 33 },
    { label: 'Mar 30', day: 43 },
    { label: 'Apr 15', day: 59 },
    { label: 'Apr 20', day: 64 },
    { label: 'Apr 30', day: 74 },
];

const GANTT_NOW_DAY: number = 11;

const GANTT_WORKER_ROWS: readonly GanttWorkerRow[] = [
    {
        id: 'sarah-mitchell',
        name: 'Sarah Mitchell',
        avatarInitials: 'SM',
        avatarBg: 'linear-gradient(135deg, #1d4ed8, #60a5fa)',
        shifts: [
            { startDay: 5,  endDay: 10, timeRange: '7AM – 7PM',  taskName: 'RN – ICU',        subLabel: 'Toronto General',    isActive: false },
            { startDay: 23, endDay: 33, timeRange: '7AM – 7PM',  taskName: 'RN – Float Pool',  subLabel: 'Sunnybrook HSC',     isActive: false },
            { startDay: 43, endDay: 59, timeRange: '7AM – 7PM',  taskName: 'RN – ER',          subLabel: "St. Michael's Hosp.", isActive: false },
            { startDay: 64, endDay: 74, timeRange: '11PM – 7AM', taskName: 'RN – Night Float', subLabel: 'Toronto Western',    isActive: false },
        ],
    },
    {
        id: 'priya-patel',
        name: 'Priya Patel',
        avatarInitials: 'PP',
        avatarBg: 'linear-gradient(135deg, #6d28d9, #a78bfa)',
        shifts: [
            { startDay: 0,  endDay: 5,  timeRange: '8AM – 4PM',  taskName: 'PSW – Ward 3B',   subLabel: 'Trillium Health',     isActive: false },
            { startDay: 10, endDay: 23, timeRange: '8AM – 4PM',  taskName: 'PSW – LTC Wing',  subLabel: 'Markham Stouffville', isActive: false },
            { startDay: 33, endDay: 43, timeRange: '4PM – 12AM', taskName: 'PSW – Memory',    subLabel: 'Sunnybrook HSC',      isActive: true },
            { startDay: 59, endDay: 64, timeRange: '8AM – 4PM',  taskName: 'PSW – Rehab',     subLabel: 'William Osler HS',    isActive: false },
        ],
    },
    {
        id: 'james-okafor',
        name: 'James Okafor',
        avatarInitials: 'JO',
        avatarBg: 'linear-gradient(135deg, #065f46, #34d399)',
        shifts: [
            { startDay: 0,  endDay: 5,  timeRange: '11PM – 7AM', taskName: 'LPN – Nights',    subLabel: "St. Michael's Hosp.",   isActive: false },
            { startDay: 10, endDay: 23, timeRange: '7AM – 3PM',  taskName: 'LPN – Med-Surg',  subLabel: 'William Osler HS',      isActive: false },
            { startDay: 43, endDay: 59, timeRange: '7AM – 3PM',  taskName: 'LPN – Oncology',  subLabel: 'Juravinski Cancer Ctr', isActive: false },
            { startDay: 64, endDay: 74, timeRange: '3PM – 11PM', taskName: 'LPN – Cardiology',subLabel: 'Hamilton Health',       isActive: false },
        ],
    },
    {
        id: 'maria-santos',
        name: 'Maria Santos',
        avatarInitials: 'MS',
        avatarBg: 'linear-gradient(135deg, #9f1239, #fb7185)',
        shifts: [
            { startDay: 5,  endDay: 10, timeRange: '7AM – 7PM',  taskName: 'RN – OR Scrub',   subLabel: 'Mount Sinai Hosp.',  isActive: false },
            { startDay: 23, endDay: 33, timeRange: '7AM – 7PM',  taskName: 'RN – OR Scrub',   subLabel: 'Mount Sinai Hosp.',  isActive: true },
            { startDay: 43, endDay: 59, timeRange: '3PM – 11PM', taskName: 'RN – PACU',       subLabel: 'Southlake Regional', isActive: false },
            { startDay: 59, endDay: 64, timeRange: '7AM – 7PM',  taskName: 'RN – ICU Step',   subLabel: 'Ottawa Hospital',    isActive: false },
            { startDay: 64, endDay: 74, timeRange: '7AM – 7PM',  taskName: 'RN – Cardiac',    subLabel: 'Southlake Regional', isActive: false },
        ],
    },
    {
        id: 'david-chen',
        name: 'David Chen',
        avatarInitials: 'DC',
        avatarBg: 'linear-gradient(135deg, #0e7490, #22d3ee)',
        shifts: [
            { startDay: 0,  endDay: 5,  timeRange: '9AM – 5PM', taskName: 'Physiotherapist', subLabel: 'Ottawa Hospital',    isActive: false },
            { startDay: 10, endDay: 23, timeRange: '9AM – 5PM', taskName: 'PT – Ortho',      subLabel: 'Kingston Health SC', isActive: false },
            { startDay: 33, endDay: 43, timeRange: '9AM – 5PM', taskName: 'PT – Rehab Unit', subLabel: 'Kingston Health SC', isActive: true },
            { startDay: 59, endDay: 64, timeRange: '9AM – 5PM', taskName: 'PT – Neuro',      subLabel: 'Brampton Civic Hosp.', isActive: false },
        ],
    },
    {
        id: 'aisha-williams',
        name: 'Aisha Williams',
        avatarInitials: 'AW',
        avatarBg: 'linear-gradient(135deg, #92400e, #fbbf24)',
        shifts: [
            { startDay: 0,  endDay: 5,  timeRange: '3PM – 11PM', taskName: 'RPN – Psych',      subLabel: 'Hamilton Health',       isActive: false },
            { startDay: 10, endDay: 23, timeRange: '7AM – 3PM',  taskName: 'RPN – Float',       subLabel: 'London Health SC',      isActive: false },
            { startDay: 33, endDay: 43, timeRange: '3PM – 11PM', taskName: 'RPN – Geriatrics',  subLabel: 'Brampton Civic Hosp.',  isActive: false },
            { startDay: 43, endDay: 59, timeRange: '11PM – 7AM', taskName: 'RPN – Crisis',      subLabel: 'CAMH Toronto',          isActive: true },
            { startDay: 64, endDay: 74, timeRange: '3PM – 11PM', taskName: 'RPN – Mental Hlth', subLabel: 'Hamilton Health',       isActive: false },
        ],
    },
];

// ── Component ───────────────────────────────────────────────────────────────

const WorkyMainDemoOpsShiftManagementScreen = (): React.ReactElement => (
    <div className='wmds-container'>

        <div className='wmds-header'>
            <button className='wmds-new-shift-btn'>
                <Plus size={12} />
                New Shift
            </button>
            <div className='wmds-stats-bar'>
                <div className='wmds-stat-item'>
                    Total <span className='wmds-stat-num'>214</span>
                </div>
                <div className='wmds-stat-sep' />
                <div className='wmds-stat-item'>
                    Filled <span className='wmds-stat-num'>198</span>
                </div>
                <div className='wmds-stat-sep' />
                <div className='wmds-stat-item'>
                    Unfilled <span className='wmds-stat-num'>16</span>
                </div>
                <div className='wmds-stat-sep' />
                <div className='wmds-stat-item wmds-stat-item--active'>
                    In Progress
                    <span className='wmds-stat-badge'>4</span>
                </div>
                <div className='wmds-stat-sep' />
                <div className='wmds-stat-item wmds-stat-dots'>···</div>
            </div>
        </div>

        <div className='wmds-gantt'>

            <div className='wmds-gantt-header'>
                <div className='wmds-gantt-gutter' />
                <div className='wmds-gantt-dates-area'>
                    {GANTT_DATE_MARKERS.map((marker: GanttDateMarker) => (
                        <div
                            key={marker.label}
                            className='wmds-date-label'
                            style={{ left: toGanttLeft(marker.day) }}
                        >
                            {marker.label}
                        </div>
                    ))}
                    <div
                        className='wmds-now-marker'
                        style={{ left: toGanttLeft(GANTT_NOW_DAY) }}
                    >
                        <div className='wmds-now-arrow' />
                        <div className='wmds-now-pill'>Now</div>
                    </div>
                </div>
            </div>

            <div className='wmds-gantt-body'>
                {GANTT_WORKER_ROWS.map((worker: GanttWorkerRow) => (
                    <div key={worker.id} className='wmds-gantt-row'>

                        <div className='wmds-worker-col'>
                            <div
                                className='wmds-worker-avatar'
                                style={{ background: worker.avatarBg }}
                            >
                                {worker.avatarInitials}
                            </div>
                            <span className='wmds-worker-name'>{worker.name}</span>
                        </div>

                        <div className='wmds-shifts-col'>

                            {GANTT_COLUMNS.map((col: GanttColumn, idx: number) => (
                                <div
                                    key={idx}
                                    className='wmds-grid-cell'
                                    style={{
                                        left: toGanttLeft(col.startDay),
                                        width: toGanttWidth(col.startDay, col.endDay),
                                    }}
                                    {...(worker.id === 'david-chen' && col.startDay === 43
                                        ? { 'data-demo-target': 'david-empty-cell' }
                                        : {})}
                                >
                                    <Plus size={11} className='wmds-grid-cell-plus' />
                                </div>
                            ))}

                            {GANTT_DATE_MARKERS.map((marker: GanttDateMarker) => (
                                <div
                                    key={marker.label}
                                    className='wmds-vline'
                                    style={{ left: toGanttLeft(marker.day) }}
                                />
                            ))}
                            <div
                                className='wmds-vline wmds-vline--now'
                                style={{ left: toGanttLeft(GANTT_NOW_DAY) }}
                            />

                            {worker.shifts.map((shift: GanttShiftBlock, idx: number) => (
                                <div
                                    key={idx}
                                    className={[
                                        'wmds-shift',
                                        shift.isActive ? 'wmds-shift--active' : 'wmds-shift--inactive',
                                        shift.endDay <= GANTT_NOW_DAY ? 'wmds-shift--past' : '',
                                    ].filter(Boolean).join(' ')}
                                    style={{
                                        left: toGanttLeft(shift.startDay),
                                        width: toGanttWidth(shift.startDay, shift.endDay),
                                    }}
                                    {...(worker.id === 'priya-patel' && shift.isActive
                                        ? { 'data-demo-target': 'priya-active-shift' }
                                        : {})}
                                >
                                    <div className='wmds-shift-top-row'>
                                        <span className='wmds-shift-time'>{shift.timeRange}</span>
                                        <span className='wmds-shift-task'>{shift.taskName}</span>
                                    </div>
                                    {shift.subLabel && (
                                        <div className='wmds-shift-sub'>{shift.subLabel}</div>
                                    )}
                                </div>
                            ))}

                        </div>
                    </div>
                ))}
            </div>

        </div>

    </div>
);


export { WorkyMainDemoOpsShiftManagementScreen };
